import { Order } from "../../../services";
import { Filter } from "../../../redux/reducers/OrdersPage/interface";
import {
	getOrderPassengerPoints,
	stringifyPoint,
} from "../../../redux/constants/OrdersPage/order";
import { isTextMatchingAnySegmentFilterHelper } from "../../../utils";

/**
 * Активен ли хоть один клиентский фильтр живых вкладок /orders
 * (поиск исполнителя/транспорта/заказа, фильтр компаний-филиалов-классов).
 */
export const hasActiveOrderPageFilters = (filters?: Filter): boolean => {
	if (!filters) return false;
	return (
		Boolean(
			filters.byExecutor?.number &&
				typeof filters.byExecutor.number === "number",
		) ||
		Boolean(
			filters.byCar?.number && typeof filters.byCar.number === "string",
		) ||
		Boolean(
			filters.byOrder &&
				["phone", "from", "to"].some(
					(key) => typeof filters.byOrder?.[key] === "string",
				),
		) ||
		Boolean(
			filters.byMulty &&
				[
					"companyIds",
					"taxiServices",
					"taxiServiceIds",
					"services",
					"classes",
				].some((key) => Array.isArray(filters.byMulty?.[key])),
		)
	);
};

/**
 * Клиентские фильтры живых вкладок /orders. Одна и та же функция для строк
 * таблицы и для счётчиков во вкладках, чтобы они не расходились.
 * `pointOnMap` — перевод "point_on_map" для адресов без улицы.
 */
export const filterOrdersByPageFilters = (
	models: Order.Model[],
	filters: Filter | undefined,
	pointOnMap: string,
): Order.Model[] => {
	if (!Array.isArray(models) || models.length === 0) return [];
	if (!filters || !hasActiveOrderPageFilters(filters)) return models;

	return models.filter((model) => {
		let retval = true;
		const [
			isOnFilterByExecutor,
			isOnFilterByCar,
			isOnFilterByOrder,
			isOnFilterByMulty,
		] = [
			Boolean(
				filters.byExecutor?.number &&
					typeof filters.byExecutor.number === "number",
			),
			Boolean(
				filters.byCar?.number &&
					typeof filters.byCar.number === "string",
			),
			Boolean(
				filters.byOrder &&
					["phone", "from", "to"].some(
						(key) => typeof filters.byOrder?.[key] === "string",
					),
			),
			Boolean(
				filters.byMulty &&
					[
						"companyIds",
						"taxiServices",
						"taxiServiceIds",
						"services",
						"classes",
					].some((key) => Array.isArray(filters.byMulty?.[key])),
			),
		];

		if (isOnFilterByExecutor && retval) {
			const aliasExecutorFind: number = filters.byExecutor?.number ?? 0;
			const arrAliasExecutorsFromModel: string[] =
				model.executorToOrder?.map(
					(executor) => executor?.executor?.callSign ?? 0,
				) || [];

			retval = arrAliasExecutorsFromModel.includes(
				`${aliasExecutorFind}`,
			);
		}

		if (isOnFilterByCar && retval) {
			const aliasCarFind: string = filters.byCar?.number ?? "";
			const typeCarFind: string = filters.byCar?.type ?? "garage";

			if (typeCarFind === "garage") {
				const callSignCarsFromOrder: string[] =
					model.carToOrder?.map((c) => c.car?.callSign ?? "") || [];

				retval = callSignCarsFromOrder.includes(aliasCarFind);
			}

			if (typeCarFind === "state") {
				const registrationNumberCarToOrder: string[] =
					model.carToOrder?.map(
						(c) =>
							c.car?.additionalFields?.registrationNumber ?? "",
					) || [];

				retval = registrationNumberCarToOrder.includes(aliasCarFind);
			}
		}

		if (isOnFilterByOrder && retval) {
			const phoneOrderFind: string | null =
				filters.byOrder?.phone ?? null;
			const fromOrderFind: string | null = filters.byOrder?.from ?? null;
			const toOrderFind: string | null = filters.byOrder?.to ?? null;

			if (phoneOrderFind && retval) {
				const phonesFromOrder = model.phones.map(
					(phone) => phone.number ?? "",
				);

				retval = phonesFromOrder.some((phone) =>
					phone.includes(phoneOrderFind),
				);
			}

			if (fromOrderFind && retval) {
				const points = getOrderPassengerPoints(model);

				const pickupFromOrder = points?.[0]
					? stringifyPoint(points[0], "address", pointOnMap)
					: "";

				retval = isTextMatchingAnySegmentFilterHelper(
					pickupFromOrder,
					fromOrderFind,
				);
			}

			if (toOrderFind && retval) {
				const points = getOrderPassengerPoints(model);

				const destinationFromOrder = points?.at(-1)
					? stringifyPoint(points.at(-1), "address", pointOnMap)
					: "";

				retval = isTextMatchingAnySegmentFilterHelper(
					destinationFromOrder,
					toOrderFind,
				);
			}
		}

		if (isOnFilterByMulty && retval) {
			const getValidArray = (arr?: number[]): number[] | null =>
				Array.isArray(arr) && arr.length ? arr : null;

			const { byMulty } = filters;

			const [
				companyIdsOrderFind,
				taxiServiceIdsOrderFind,
				servicesOrderFind,
				classesOrderFind,
			] = [
				getValidArray(byMulty?.companyIds),
				getValidArray(byMulty?.taxiServiceIds),
				getValidArray(byMulty?.services),
				getValidArray(byMulty?.classes),
			];

			if (companyIdsOrderFind && retval) {
				const companyFromOrder = model.taxiService?.company?.id ?? -1;

				retval = companyIdsOrderFind.includes(companyFromOrder);
			}

			if (taxiServiceIdsOrderFind && retval) {
				const taxiServiceFromOrder = model.taxiService?.id ?? -1;

				retval = taxiServiceIdsOrderFind.includes(taxiServiceFromOrder);
			}

			if (classesOrderFind && retval) {
				const carClassessFromOrder =
					model.orderToCarClasses?.map(
						(item) => item?.carClass?.id || -1,
					) || [];

				retval = carClassessFromOrder.some((num) =>
					classesOrderFind.includes(num),
				);
			}

			if (servicesOrderFind && retval) {
				const servicesFromOrder =
					model.orderToServices?.map(
						(item) => item?.service?.id || -1,
					) || [];

				retval = servicesFromOrder.some((num) =>
					servicesOrderFind.includes(num),
				);
			}
		}

		return retval;
	});
};
